import type {ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {buildProgressiveDiffEntries} from '../../utils/build-progressive-diff-entries'
import type {StateCreator} from 'zustand'
import type {DiffEntriesSlice} from './diff-entries'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'
import {makeProgressiveDiffLoader, type ProgressiveDiffLoader} from '../../utils/progressive-diffs-loader'
import type {ChangesLoadTiming} from '../../utils/changes-load-timing'

export interface ProgressiveDiffsSlice {
  // Hashmap by pathDigest
  entriesMap: Record<string, ProgressiveDiffEntry>
  // Ordered list of pathDigests to maintain iteration order
  entriesOrder: string[]

  selectedPathDigest: string | undefined

  // Getter methods to expose external state when called (no Zustand subscription)
  getSelectedPathDigest: () => string | undefined

  getProgressiveDiffEntryByPathDigest: (pathDigest?: string) => ProgressiveDiffEntry | undefined

  // Public actions, intended for use outside the store.
  progressiveDiffsActions: ProgressiveDiffLoader & {
    setRecordPriorityLoad: (callback: ChangesLoadTiming['recordPriorityLoad'] | undefined) => void
    updateSelectedPathDigest: (pathDigest: string | undefined) => void
  }
}

export type ProgressiveDiffSliceOpts = {
  initialLoadedPathDigests: Set<string>
  selectedPathDigest: string | undefined
  getIsComparisonOutdated?: () => boolean
  recordPriorityLoad?: ChangesLoadTiming['recordPriorityLoad']
} & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>

export const createProgressiveDiffsSlice =
  ({
    initialDiffSummaries,
    initialLoadedPathDigests,
    selectedPathDigest: initialSelectedPathDigest,
    getIsComparisonOutdated,
    recordPriorityLoad,
  }: ProgressiveDiffSliceOpts): StateCreator<
    ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice,
    [['zustand/devtools', never]],
    [],
    ProgressiveDiffsSlice
  > =>
  (set, get) => {
    let currentRecordPriorityLoad = recordPriorityLoad
    const progressiveDiffLoader = makeProgressiveDiffLoader({
      get,
      initialLoadedPathDigests,
      getIsComparisonOutdated,
    })
    const loadPriority = (pathDigest?: string) => {
      if (pathDigest) currentRecordPriorityLoad?.([pathDigest])
      progressiveDiffLoader.loadPriority(pathDigest)
    }
    const loadManyPriority = (pathDigests: string[]) => {
      if (pathDigests.length > 0) currentRecordPriorityLoad?.(pathDigests)
      progressiveDiffLoader.loadManyPriority(pathDigests)
    }

    // Build initial entries
    const {entriesMap: initialEntriesMap, entriesOrder: initialEntriesOrder} = buildProgressiveDiffEntries({
      diffSummaries: initialDiffSummaries,
      loadedPathDigests: initialLoadedPathDigests,
    })

    return {
      entriesMap: initialEntriesMap,
      entriesOrder: initialEntriesOrder,
      selectedPathDigest: initialSelectedPathDigest,

      getSelectedPathDigest: () => get().selectedPathDigest,
      getProgressiveDiffEntryByPathDigest: (pathDigest?: string) =>
        pathDigest ? get().entriesMap[pathDigest] : undefined,

      progressiveDiffsActions: {
        ...progressiveDiffLoader,
        loadPriority,
        loadManyPriority,
        setRecordPriorityLoad: callback => {
          currentRecordPriorityLoad = callback
        },
        updateSelectedPathDigest: (pathDigest: string | undefined) => {
          set({selectedPathDigest: pathDigest}, undefined, 'updateSelectedPathDigest')
          if (pathDigest) {
            // diff loader filters out any previous loaded digests
            loadPriority(pathDigest)
          }
        },
      },
    }
  }
